import { useState } from "react";

import { css } from "@emotion/react";
import MuiAutocomplete, {
    AutocompleteProps as MuiAutocompleteProps
} from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

import {
    AlertIcon,
    colors,
    ExpandMoreIcon
} from "@dotnet/telematics-design-system";

import HighlightedOption from "components/HighlightedOption";
import { noop } from "shared/functions";
import { svgFontSize } from "shared/styles/styleFunctions";

import Checkbox from "../Checkbox";
import { useInputFieldStyles } from "../InputField";
import Label from "../Label";
import Tag, { TAG_COLORS, TAG_SIZES } from "../Tag";
import { TOOLTIP_PLACEMENT, TooltipLarge } from "../Tooltips";
import { AutocompleteProps } from "./types";

const Autocomplete = ({
    size,
    labelLeft,
    labelIcon,
    labelRight,
    iconLeft,
    iconRight,
    hasCheckbox,
    errorText,
    isRequired,
    width,
    tooltipProps,
    textFieldParams = {},
    customStyle,
    ...props
}: AutocompleteProps &
    Omit<
        MuiAutocompleteProps<any, boolean, boolean, boolean>,
        "renderInput" | "size"
    >) => {
    const {
        blue200,
        gray100,
        gray200,
        gray500,
        gray700,
        gray900,
        red700,
        textDisabled,
        textPlaceholder,
        white
    } = colors;

    const {
        disabled,
        fullWidth,
        loading,
        multiple,
        options,
        popupIcon,
        readOnly,
        slotProps,
        getOptionDisabled,
        getOptionLabel,
        onClose,
        renderOption,
        renderValue
    } = props;

    const {
        PADDINGS_TOP_OR_BOTTOM,
        muiOutlinedInputStyles,
        circularProgressSize
    } = useInputFieldStyles();

    const [inputValue, setInputValue] = useState("");

    const getWidth = () => {
        if (fullWidth) {
            return "100%";
        }

        return width || "unset";
    };

    const containerWidth = getWidth();
    const renderTagsInside = multiple && !renderValue;

    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                width: containerWidth,
                ...customStyle
            })}
        >
            {(labelLeft || labelRight) && (
                <Label
                    titleLeft={labelLeft}
                    titleRight={labelRight}
                    titleTooltip={labelIcon}
                    isRequired={isRequired}
                />
            )}

            <MuiAutocomplete
                {...props}
                options={options}
                autoComplete
                autoHighlight
                disableCloseOnSelect={multiple}
                popupIcon={
                    popupIcon ?? (
                        <ExpandMoreIcon
                            css={css({
                                color: gray500
                            })}
                        />
                    )
                }
                renderInput={(params: any) => {
                    return (
                        <TooltipLarge
                            {...tooltipProps}
                            title={tooltipProps?.title || ""}
                            placement={TOOLTIP_PLACEMENT.Top}
                            customMargin="0 0 6px 0"
                        >
                            <TextField
                                {...params}
                                {...textFieldParams}
                                error={Boolean(errorText)}
                                onKeyDown={e => {
                                    if (e.key === "Backspace") {
                                        e.stopPropagation();
                                    }
                                }}
                                onChange={e => {
                                    setInputValue(e.target.value);
                                    (textFieldParams as any).onChange?.(e);
                                }}
                                helperText={
                                    errorText ? (
                                        <>
                                            <AlertIcon
                                                css={css({
                                                    color: red700,
                                                    marginRight: "4px",
                                                    ...svgFontSize(16)
                                                })}
                                            />
                                            {errorText}
                                        </>
                                    ) : (
                                        ""
                                    )
                                }
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        spellCheck: "false",
                                        ...(iconLeft && {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    {iconLeft}
                                                </InputAdornment>
                                            )
                                        }),
                                        ...((iconRight || loading) && {
                                            endAdornment: (
                                                <InputAdornment position="start">
                                                    {loading && (
                                                        <CircularProgress
                                                            size={circularProgressSize(
                                                                size
                                                            )}
                                                            sx={{
                                                                color: gray500
                                                            }}
                                                        />
                                                    )}

                                                    {iconRight || null}
                                                    {
                                                        params.InputProps
                                                            .endAdornment
                                                    }
                                                </InputAdornment>
                                            )
                                        })
                                    }
                                }}
                                sx={{
                                    "& .MuiTouchRipple-root": {
                                        display: "none"
                                    },

                                    "& .MuiFormHelperText-root": {
                                        margin: "6px 0 0",
                                        display: "flex",
                                        alignItems: "center",
                                        color: gray700,
                                        fontSize: "14px",
                                        lineHeight: "20px",
                                        fontWeight: "500",
                                        letterSpacing: "0.1px",

                                        "&.Mui-error": {
                                            color: gray700
                                        }
                                    },

                                    "& .MuiInputAdornment-root": {
                                        height: "0px"
                                    },

                                    "& .MuiAutocomplete-clearIndicator": {
                                        "& svg": {
                                            fontSize: "16px"
                                        }
                                    }
                                }}
                            />
                        </TooltipLarge>
                    );
                }}
                renderValue={
                    renderTagsInside
                        ? (value, getItemProps) =>
                              value.map((option: any, index: number) => {
                                  const { key, onDelete, ...tagProps } =
                                      getItemProps({
                                          index
                                      }) as any;

                                  const isTagDisabled =
                                      disabled ||
                                      Boolean(getOptionDisabled?.(option));

                                  return (
                                      <Tag
                                          key={key}
                                          {...tagProps}
                                          size={TAG_SIZES.Tiny}
                                          color={TAG_COLORS.Primary}
                                          title={
                                              getOptionLabel
                                                  ? getOptionLabel(option)
                                                  : (option.name ?? option)
                                          }
                                          onDelete={onDelete ?? noop}
                                          isReadOnly={readOnly}
                                          disabled={isTagDisabled}
                                      />
                                  );
                              })
                        : renderValue
                }
                renderOption={
                    renderOption ??
                    ((propsData, options, state) => {
                        const { key, ...restProps } = propsData;

                        const label = getOptionLabel?.(options) ?? options;
                        const isOptionDisabled = propsData["aria-disabled"];

                        return multiple && hasCheckbox ? (
                            <li key={key}>
                                <span {...restProps}>
                                    <div
                                        css={css({
                                            display: "flex",
                                            flexDirection: "column"
                                        })}
                                    >
                                        <span>
                                            <HighlightedOption
                                                text={label}
                                                query={inputValue}
                                            />
                                        </span>
                                    </div>

                                    <div
                                        css={css({
                                            marginLeft: "auto",
                                            alignSelf: "flex-start"
                                        })}
                                    >
                                        <Checkbox
                                            checked={state.selected}
                                            disabled={Boolean(isOptionDisabled)}
                                        />
                                    </div>
                                </span>
                            </li>
                        ) : (
                            <li key={key} {...restProps}>
                                <span>
                                    <HighlightedOption
                                        text={label}
                                        query={inputValue}
                                    />
                                </span>
                            </li>
                        );
                    })
                }
                onClose={(event, reason) => {
                    setInputValue("");
                    onClose?.(event, reason);
                }}
                slotProps={{
                    paper: {
                        sx: {
                            border: `1px solid ${gray200}`,
                            borderRadius: "6px",
                            boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.16)",

                            "& .MuiAutocomplete-listbox": {
                                padding: "0",

                                "& .MuiAutocomplete-option": {
                                    color: gray700,
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    lineHeight: "20px",
                                    letterSpacing: "0.1px",
                                    padding: "14px 16px",
                                    wordBreak: "break-word"
                                },

                                "& .MuiAutocomplete-option.Mui-focused": {
                                    background: gray100
                                },

                                '& .MuiAutocomplete-option[aria-selected="true"], .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
                                    {
                                        background: multiple ? white : blue200,

                                        "&:hover": {
                                            background: multiple
                                                ? gray100
                                                : blue200
                                        }
                                    },

                                '& .MuiAutocomplete-option[aria-disabled="true"]':
                                    {
                                        opacity: "1",
                                        color: textDisabled
                                    }
                            },

                            "& .MuiAutocomplete-loading, .MuiAutocomplete-noOptions":
                                {
                                    color: textPlaceholder,
                                    fontSize: "14px",
                                    lineHeight: "20px"
                                }
                        }
                    },
                    popper: renderTagsInside
                        ? {
                              ...slotProps?.popper,
                              modifiers: [
                                  {
                                      name: "offset",
                                      options: {
                                          offset: [
                                              0,
                                              PADDINGS_TOP_OR_BOTTOM[size]
                                          ]
                                      }
                                  }
                              ]
                          }
                        : slotProps?.popper
                }}
                sx={[
                    {
                        "& .MuiTextField-root": {
                            ...(renderTagsInside && {
                                maxHeight: "113px",
                                boxSizing: "border-box",
                                paddingTop: `${PADDINGS_TOP_OR_BOTTOM[size]}px`,
                                paddingBottom: `${PADDINGS_TOP_OR_BOTTOM[size]}px`,
                                backgroundColor:
                                    disabled || readOnly ? gray100 : "unset"
                            })
                        },

                        "& .MuiOutlinedInput-root": {
                            ...(renderTagsInside && {
                                overflowY: loading ? "hidden" : "auto",
                                position: "static",

                                "&::-webkit-scrollbar-thumb": {
                                    cursor: "auto"
                                }
                            }),

                            "& .MuiAutocomplete-input": {
                                color: gray900,
                                fontSize: "14px",
                                fontWeight: "500",
                                lineHeight: "20px",
                                letterSpacing: "0.1px",
                                padding: "0"
                            },

                            "& .MuiAutocomplete-endAdornment": {
                                right: "12px",

                                "& > button": {
                                    backgroundColor: "unset"
                                },

                                "& .MuiAutocomplete-popupIndicator": {
                                    marginRight: "0"
                                }
                            },

                            ...muiOutlinedInputStyles(size, disabled, readOnly),

                            ...(renderTagsInside && {
                                paddingTop: "0",
                                paddingBottom: "0"
                            })
                        },

                        "& .MuiAutocomplete-inputRoot": {
                            gap: "3px"
                        },

                        "& .MuiAutocomplete-tag": {
                            margin: "0"
                        }
                    }
                ]}
            />
        </div>
    );
};

export * from "./types";
export default Autocomplete;
